import MetaContent from '../../components/MetaContent'
import RateEstimateForm, {
  ACCESSORIALS,
  RateEstimateFormValues,
} from '../../components/forms/RateEstimateForm'
import {
  Quote,
  QuoteResponse,
} from '../../components/forms/results/QuoteResults'
import { attributes } from '../../content/rate-estimate.md'
import { useFirstInteraction } from '../../components/hooks/useFirstInteraction'
import { useRouter } from 'next/router'
import Script from 'next/script'
import React, { useEffect, useState } from 'react'

const quoteToFormValues = (quote: Quote): Partial<RateEstimateFormValues> => {
  const values: Partial<RateEstimateFormValues> = {}

  if (quote.originZipCode != null) {
    values.originZip = String(quote.originZipCode)
  }
  if (quote.originAddressLine1) {
    values.originAddressLine1 = quote.originAddressLine1
  }
  if (quote.originAddressLine2) {
    values.originAddressLine2 = quote.originAddressLine2
  }
  if (quote.originCity) {
    values.originCity = quote.originCity
  }
  if (quote.originState) {
    values.originState = quote.originState
  }
  if (quote.originZip != null) {
    values.originZip = String(quote.originZip)
  }
  if (quote.originCountry) {
    values.originCountry = quote.originCountry
  }
  if (quote.destinationZipCode != null) {
    values.destinationZip = String(quote.destinationZipCode)
  }
  if (quote.destinationAddressLine1) {
    values.destinationAddressLine1 = quote.destinationAddressLine1
  }
  if (quote.destinationAddressLine2) {
    values.destinationAddressLine2 = quote.destinationAddressLine2
  }
  if (quote.destinationCity) {
    values.destinationCity = quote.destinationCity
  }
  if (quote.destinationState) {
    values.destinationState = quote.destinationState
  }
  if (quote.destinationZip != null) {
    values.destinationZip = String(quote.destinationZip)
  }
  if (quote.destinationCountry) {
    values.destinationCountry = quote.destinationCountry
  }

  const shipmentItems = (quote.details ?? [])
    .map((detail) => ({
      weight: detail.weight != null ? String(detail.weight) : '',
      freightClass: detail.nmfcClass != null ? String(detail.nmfcClass) : '',
      length: detail.length != null ? Number(detail.length) : null,
      width: detail.width != null ? Number(detail.width) : null,
      height: detail.height != null ? Number(detail.height) : null,
      handlingUnits: detail.units != null ? Number(detail.units) : null,
      hazardous: false,
      description: '',
    }))
    .filter((item) =>
      [
        item.weight,
        item.freightClass,
        item.length,
        item.width,
        item.height,
        item.handlingUnits,
      ].some((value) => value !== '')
    )
  if (shipmentItems.length > 0) {
    values.shipmentItems = shipmentItems
  }

  if (Array.isArray(quote.accessorials) && quote.accessorials.length > 0) {
    const selectableAccessorials = quote.accessorials.filter((code) =>
      ACCESSORIALS.some((accessorial) => accessorial.code === code)
    )
    if (selectableAccessorials.length > 0) {
      values.accessorials = selectableAccessorials
    }
  }

  return values
}

export default function RateEstimate() {
  const router = useRouter()
  const { metaTitle, metaDescription } = attributes
  const googleMapsApiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY

  const [initialValues, setInitialValues] = useState<
    Partial<RateEstimateFormValues> | undefined
  >(undefined)
  const [googlePlacesReady, setGooglePlacesReady] = useState(false)
  // Load the Google Maps script only after the visitor interacts with the
  // page, so no third-party request fires on bare page load.
  const interacted = useFirstInteraction()

  const requoteParam = router.query.requote
  const quoteIdParam = router.query.quoteId
  const quoteLookupParam = quoteIdParam ?? requoteParam

  useEffect(() => {
    if (!router.isReady) return

    if (
      !quoteLookupParam ||
      (typeof quoteLookupParam === 'string' && quoteLookupParam.trim() === '')
    ) {
      setInitialValues(undefined)
      return
    }

    const requoteId = Array.isArray(quoteLookupParam)
      ? quoteLookupParam[0]
      : quoteLookupParam

    let cancelled = false

    fetch(`/api/internal-api/quotes/${encodeURIComponent(requoteId)}`)
      .then(async (res) => {
        if (cancelled) return
        if (!res.ok) {
          return
        }
        const data: QuoteResponse = await res.json()
        const quote = data.quote
        if (!quote) {
          return
        }
        setInitialValues(quoteToFormValues(quote))
      })
      .catch(() => {
        if (cancelled) return
      })

    return () => {
      cancelled = true
    }
  }, [router.isReady, quoteLookupParam])

  return (
    <>
      <MetaContent title={metaTitle} description={metaDescription} />
      {googleMapsApiKey && interacted && (
        <Script
          id="google-places"
          src={`https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
            googleMapsApiKey
          )}&v=weekly&libraries=places`}
          strategy="afterInteractive"
          onLoad={() => setGooglePlacesReady(true)}
          onReady={() => setGooglePlacesReady(true)}
        />
      )}

      <main>
        <section className="px-4 py-8 m-auto md:px-11 md:py-12 lg:py-20 max-w-[1440px] w-full flex items-start">
          <RateEstimateForm
            initialValues={initialValues}
            googlePlacesReady={googlePlacesReady}
          />
        </section>
      </main>
    </>
  )
}
